// client/src/pages/CustomersPage.jsx
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  Car, Users, Plus, X, ChevronDown, ChevronRight, Search, Edit2,
} from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'
import { useCustomers, useCustomer, useAddCustomer, useVehicles, useAddVehicle } from '../api/hooks'

// ─── Constants ────────────────────────────────────────────────────────────────

const inputCls = `w-full px-3 py-2 text-sm bg-[var(--bg-base)] border border-[var(--border)]
  rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
  focus:outline-none focus:border-indigo-500 transition-colors`

const FUEL_OPTIONS  = ['petrol', 'diesel', 'electric', 'hybrid', 'other']
const TRANS_OPTIONS = ['manual', 'automatic', 'other']

// ─── Plate ────────────────────────────────────────────────────────────────────

function Plate({ registration }) {
  return (
    <span className="inline-block font-mono font-bold tracking-widest text-xs
                     bg-yellow-400 text-black px-2 py-0.5 rounded">
      {registration}
    </span>
  )
}

// ─── Add Customer modal ───────────────────────────────────────────────────────

function AddCustomerModal({ onClose }) {
  const addCustomer = useAddCustomer()
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '' })
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      await addCustomer.mutateAsync(form)
      toast.success('Customer added')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add customer')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">Add Customer</h2>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {[
            { label: 'Full name', field: 'name',    type: 'text',  required: true, placeholder: 'Jane Smith' },
            { label: 'Email',     field: 'email',   type: 'email',                 placeholder: 'jane@example.com' },
            { label: 'Phone',     field: 'phone',   type: 'tel',                   placeholder: '07700 900 123' },
            { label: 'Address',   field: 'address', type: 'text',                  placeholder: '12 High Street, Town' },
          ].map(({ label, field, type, required, placeholder }) => (
            <div key={field}>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">{label}</label>
              <input type={type} value={form[field]} onChange={set(field)} placeholder={placeholder}
                required={required} className={inputCls} />
            </div>
          ))}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={addCustomer.isPending}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                         text-white text-sm font-medium rounded-lg transition-colors">
              {addCustomer.isPending ? 'Adding…' : 'Add Customer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Add Vehicle modal ────────────────────────────────────────────────────────

function AddVehicleModal({ customers, garageId, onClose }) {
  const qc = useQueryClient()
  const addVehicle = useAddVehicle()

  const [form, setForm] = useState({
    registration: '', make: '', model: '', year: '', colour: '',
    engineSize: '', fuelType: '', transmission: '',
  })
  const [customerId, setCustomerId] = useState('')

  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.registration.trim() || !form.make.trim() || !form.model.trim()) {
      toast.error('Registration, make and model are required')
      return
    }
    try {
      await addVehicle.mutateAsync({
        ...form,
        primaryGarageId: garageId,
        ...(customerId ? { customerId } : {}),
      })
      if (customerId) {
        qc.invalidateQueries({ queryKey: ['customer', customerId] })
      }
      toast.success('Vehicle added')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add vehicle')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-lg bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">Add Vehicle</h2>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Customer assignment */}
          {customers.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
                Assign to customer <span className="text-[var(--text-muted)] font-normal">(optional)</span>
              </label>
              <select value={customerId} onChange={e => setCustomerId(e.target.value)} className={inputCls}>
                <option value="">— No customer —</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Registration *</label>
              <input
                type="text"
                value={form.registration}
                onChange={set('registration')}
                placeholder="e.g. AB12 CDE"
                className={inputCls}
                required
              />
            </div>
            {[
              { label: 'Make *',  field: 'make',       placeholder: 'e.g. Ford'  },
              { label: 'Model *', field: 'model',      placeholder: 'e.g. Focus' },
              { label: 'Year',    field: 'year',       placeholder: 'e.g. 2019'  },
              { label: 'Colour',  field: 'colour',     placeholder: 'e.g. White' },
              { label: 'Engine',  field: 'engineSize', placeholder: 'e.g. 2.0L'  },
            ].map(({ label, field, placeholder }) => (
              <div key={field}>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">{label}</label>
                <input
                  type={field === 'year' ? 'number' : 'text'}
                  value={form[field]}
                  onChange={set(field)}
                  placeholder={placeholder}
                  className={inputCls}
                />
              </div>
            ))}
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Fuel type</label>
              <select value={form.fuelType} onChange={set('fuelType')} className={inputCls}>
                <option value="">—</option>
                {FUEL_OPTIONS.map(f => (
                  <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Transmission</label>
              <select value={form.transmission} onChange={set('transmission')} className={inputCls}>
                <option value="">—</option>
                {TRANS_OPTIONS.map(t => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={addVehicle.isPending}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                         text-white text-sm font-medium rounded-lg transition-colors">
              {addVehicle.isPending ? 'Adding…' : 'Add Vehicle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Expanded vehicles (customer view) ────────────────────────────────────────

function ExpandedVehicles({ customerId }) {
  const navigate = useNavigate()
  const { data: customer, isLoading } = useCustomer(customerId)

  if (isLoading) {
    return (
      <div className="px-5 pb-4 pt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="h-20 bg-[var(--bg-overlay)] rounded-xl animate-pulse" />
        ))}
      </div>
    )
  }

  const vehicles = customer?.vehicles || []

  if (vehicles.length === 0) {
    return (
      <div className="px-5 pb-4 pt-2">
        <p className="text-sm text-[var(--text-muted)]">No vehicles registered.</p>
      </div>
    )
  }

  return (
    <div className="px-5 pb-4 pt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
      {vehicles.map(v => (
        <button
          key={v.id}
          onClick={() => navigate(`/garage/vehicles/${v.id}`)}
          className="text-left p-3 bg-[var(--bg-base)] border border-[var(--border)]
                     rounded-xl hover:border-indigo-500/50 hover:bg-indigo-500/5
                     transition-all group"
        >
          <Plate registration={v.registration} />
          <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-indigo-400
                         transition-colors mt-1.5 truncate">
            {v.make} {v.model}
          </p>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">
            {v.year || ''}
            {v.colour ? (v.year ? ` · ${v.colour}` : v.colour) : ''}
          </p>
          {v.currentMileage && (
            <p className="text-xs text-[var(--text-muted)]">
              {Number(v.currentMileage).toLocaleString()} miles
            </p>
          )}
        </button>
      ))}
    </div>
  )
}

// ─── Customer row ─────────────────────────────────────────────────────────────

function CustomerRow({ customer, isAdmin }) {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(false)

  return (
    <div className={clsx(
      'bg-[var(--bg-surface)] border rounded-xl transition-all overflow-hidden',
      expanded ? 'border-[var(--border-strong)]' : 'border-[var(--border)]'
    )}>
      <div className="flex items-center gap-4 px-5 py-4">
        <div className="w-9 h-9 rounded-full bg-indigo-600/15 flex items-center justify-center shrink-0">
          <span className="text-sm font-semibold text-indigo-400">
            {customer.name[0]?.toUpperCase()}
          </span>
        </div>

        <button className="flex-1 min-w-0 text-left" onClick={() => setExpanded(e => !e)}>
          <p className="font-medium text-[var(--text-primary)] truncate">{customer.name}</p>
          <p className="text-xs text-[var(--text-muted)] truncate">
            {customer.email || customer.phone || 'No contact details'}
          </p>
        </button>

        <div className="flex items-center gap-2 shrink-0">
          <div className="flex items-center gap-1 text-xs text-[var(--text-muted)]">
            <Car size={13} />
            <span>{customer.vehicle_count ?? 0}</span>
          </div>

          {isAdmin && (
            <button
              onClick={() => navigate(`/garage/customers/${customer.id}`)}
              title="Edit customer"
              className="p-1.5 text-[var(--text-muted)] hover:text-indigo-400
                         hover:bg-indigo-500/10 rounded-lg transition-colors"
            >
              <Edit2 size={13} />
            </button>
          )}

          <button
            onClick={() => setExpanded(e => !e)}
            className="p-1.5 text-[var(--text-muted)] hover:text-[var(--text-primary)]
                       hover:bg-[var(--bg-overlay)] rounded-lg transition-colors"
          >
            <ChevronDown
              size={16}
              className={clsx('transition-transform duration-200', expanded && 'rotate-180')}
            />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-[var(--border)]">
          <ExpandedVehicles customerId={customer.id} />
        </div>
      )}
    </div>
  )
}

// ─── Vehicle row (vehicle view) ───────────────────────────────────────────────

function VehicleRow({ vehicle, customer, isAdmin }) {
  const navigate = useNavigate()

  const meta = [
    vehicle.year,
    vehicle.colour,
    vehicle.owner_name || (customer?.name),
  ].filter(Boolean).join(' · ')

  return (
    <div className="flex items-center bg-[var(--bg-surface)] border border-[var(--border)]
                    rounded-xl hover:border-[var(--border-strong)] transition-all overflow-hidden group">
      <button
        onClick={() => navigate(`/garage/vehicles/${vehicle.id}`)}
        className="flex items-center gap-4 px-5 py-4 flex-1 min-w-0 text-left"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <Plate registration={vehicle.registration} />
            <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-indigo-400
                           transition-colors truncate">
              {vehicle.make} {vehicle.model}
            </p>
          </div>
          {meta && <p className="text-xs text-[var(--text-muted)]">{meta}</p>}
        </div>
        <ChevronRight size={15} className="text-[var(--text-muted)] group-hover:text-indigo-400 shrink-0" />
      </button>

      {/* Customer edit shortcut */}
      {isAdmin && customer && (
        <button
          onClick={() => navigate(`/garage/customers/${customer.id}`)}
          title={`Edit ${customer.name}`}
          className="px-3 py-4 border-l border-[var(--border)] text-[var(--text-muted)]
                     hover:text-indigo-400 hover:bg-indigo-500/5 transition-colors shrink-0"
        >
          <Edit2 size={13} />
        </button>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function CustomersPage() {
  const { user } = useAuth()
  const { data: customers = [], isLoading: cLoading } = useCustomers()
  const { data: vehicles  = [], isLoading: vLoading  } = useVehicles()
  const [viewStyle,       setViewStyle]       = useState('customer')
  const [search,          setSearch]          = useState('')
  const [showAddCustomer, setShowAddCustomer] = useState(false)
  const [showAddVehicle,  setShowAddVehicle]  = useState(false)

  const isAdmin   = user?.role === 'garage_admin' || user?.role === 'platform_admin'
  const isLoading = viewStyle === 'customer' ? cLoading : vLoading

  // Derive the garage ID from existing data so we can pass it to the Add Vehicle modal
  const garageId = customers[0]?.garage_id || vehicles[0]?.primary_garage_id || null

  // Build a map: user_id → customer record (for cross-referencing from vehicle rows)
  const customerByUserId = Object.fromEntries(
    customers.filter(c => c.user_id).map(c => [c.user_id, c])
  )

  // Filtered customers
  const q = search.toLowerCase()
  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(q) ||
    c.email?.toLowerCase().includes(q) ||
    c.phone?.includes(search)
  )

  // Filtered + sorted vehicles
  const filteredVehicles = vehicles
    .filter(v =>
      v.registration?.toLowerCase().includes(q) ||
      v.make?.toLowerCase().includes(q) ||
      v.model?.toLowerCase().includes(q) ||
      v.owner_name?.toLowerCase().includes(q) ||
      v.owner_email?.toLowerCase().includes(q)
    )
    .slice()
    .sort((a, b) => (a.registration || '').localeCompare(b.registration || ''))

  const isCustomerView = viewStyle === 'customer'
  const totalCount     = isCustomerView ? customers.length : vehicles.length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">
            Customer Vehicles
          </h1>
          {!isLoading && totalCount > 0 && (
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              {isCustomerView
                ? `${customers.length} ${customers.length === 1 ? 'customer' : 'customers'}`
                : `${vehicles.length} ${vehicles.length === 1 ? 'vehicle' : 'vehicles'}`}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* View toggle */}
          <div className="flex items-center gap-0.5 p-0.5 bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg">
            <button
              onClick={() => { setViewStyle('customer'); setSearch('') }}
              className={clsx(
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium transition-colors',
                isCustomerView
                  ? 'bg-indigo-600 text-white'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              )}
            >
              <Users size={13} /> Customers
            </button>
            <button
              onClick={() => { setViewStyle('vehicle'); setSearch('') }}
              className={clsx(
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium transition-colors',
                !isCustomerView
                  ? 'bg-indigo-600 text-white'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              )}
            >
              <Car size={13} /> Vehicles
            </button>
          </div>

          {isAdmin && isCustomerView && (
            <button
              onClick={() => setShowAddCustomer(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700
                         text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Plus size={16} />
              Add Customer
            </button>
          )}

          {isAdmin && !isCustomerView && garageId && (
            <button
              onClick={() => setShowAddVehicle(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700
                         text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Plus size={16} />
              Add Vehicle
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      {totalCount > 0 && (
        <div className="relative max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            type="text"
            placeholder={isCustomerView ? 'Search customers…' : 'Search by plate, make, model or owner…'}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-[var(--bg-surface)] border border-[var(--border)]
                       rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
                       focus:outline-none focus:border-indigo-500 transition-colors"
          />
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-16 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl animate-pulse" />
          ))}
        </div>
      ) : isCustomerView ? (
        /* ── Customer view ── */
        customers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border)]
                            flex items-center justify-center mb-4">
              <Users size={28} className="text-[var(--text-muted)]" />
            </div>
            <h3 className="font-display font-bold text-lg text-[var(--text-primary)] mb-2">No customers yet</h3>
            <p className="text-sm text-[var(--text-secondary)] mb-6 max-w-xs">
              Customers will appear here once they are linked to your garage, or you can add them manually.
            </p>
            {isAdmin && (
              <button onClick={() => setShowAddCustomer(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700
                           text-white text-sm font-medium rounded-lg transition-colors">
                <Plus size={16} />
                Add first customer
              </button>
            )}
          </div>
        ) : filteredCustomers.length === 0 ? (
          <p className="text-sm text-[var(--text-secondary)] py-8 text-center">
            No customers match your search.
          </p>
        ) : (
          <div className="space-y-2">
            {filteredCustomers.map(c => (
              <CustomerRow key={c.id} customer={c} isAdmin={isAdmin} />
            ))}
          </div>
        )
      ) : (
        /* ── Vehicle view ── */
        vehicles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border)]
                            flex items-center justify-center mb-4">
              <Car size={28} className="text-[var(--text-muted)]" />
            </div>
            <h3 className="font-display font-bold text-lg text-[var(--text-primary)] mb-2">No vehicles yet</h3>
            <p className="text-sm text-[var(--text-secondary)] max-w-xs mb-6">
              Vehicles registered at your garage will appear here.
            </p>
            {isAdmin && garageId && (
              <button
                onClick={() => setShowAddVehicle(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700
                           text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Plus size={16} />
                Add first vehicle
              </button>
            )}
          </div>
        ) : filteredVehicles.length === 0 ? (
          <p className="text-sm text-[var(--text-secondary)] py-8 text-center">
            No vehicles match your search.
          </p>
        ) : (
          <div className="space-y-2">
            {filteredVehicles.map(v => (
              <VehicleRow
                key={v.id}
                vehicle={v}
                customer={v.owner_id ? customerByUserId[v.owner_id] : null}
                isAdmin={isAdmin}
              />
            ))}
          </div>
        )
      )}

      {showAddCustomer && <AddCustomerModal onClose={() => setShowAddCustomer(false)} />}
      {showAddVehicle  && (
        <AddVehicleModal
          customers={customers}
          garageId={garageId}
          onClose={() => setShowAddVehicle(false)}
        />
      )}
    </div>
  )
}
